import React, { useMemo } from 'react';
import { Layers } from 'lucide-react';

const EntityTypeSunburst = ({ data }) => {
  const sunburstData = useMemo(() => {
    const entityGroups = {};
    
    data.forEach(item => {
      const type = item.entityType || 'UNKNOWN';
      if (!entityGroups[type]) {
        entityGroups[type] = {
          healthy: [],
          degraded: [],
          unhealthy: [],
          unknown: []
        };
      }
      
      if (item.health === 'HEALTHY') entityGroups[type].healthy.push(item);
      else if (item.health === 'DEGRADED') entityGroups[type].degraded.push(item);
      else if (item.health === 'UNHEALTHY') entityGroups[type].unhealthy.push(item);
      else entityGroups[type].unknown.push(item);
    });

    return Object.entries(entityGroups)
      .map(([type, groups]) => ({
        type,
        total: groups.healthy.length + groups.degraded.length + groups.unhealthy.length + groups.unknown.length,
        healthy: groups.healthy.length,
        degraded: groups.degraded.length,
        unhealthy: groups.unhealthy.length,
        unknown: groups.unknown.length
      }))
      .sort((a, b) => b.total - a.total);
  }, [data]);

  const total = data.length;
  const colors = {
    healthy: '#10b981',
    degraded: '#eab308',
    unhealthy: '#ef4444',
    unknown: '#6b7280'
  };

  return (
    <div className="bg-dark-card rounded-lg border border-dark-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <Layers size={24} className="text-cyan-accent" />
        <h3 className="text-xl font-bold text-cyan-accent">Entity Type Distribution</h3>
      </div>

      {/* Center Circle - Total */}
      <div className="flex items-center justify-center mb-8">
        <div className="relative w-48 h-48 rounded-full bg-gradient-to-br from-cyan-accent/20 to-purple-accent/20 border-4 border-cyan-accent/40 flex items-center justify-center"
             style={{ boxShadow: '0 0 40px rgba(0, 229, 255, 0.3)' }}>
          <div className="text-center">
            <div className="text-5xl font-bold text-cyan-accent">{total}</div>
            <div className="text-sm text-gray-400 mt-2">Total Changes</div>
          </div>
        </div>
      </div>

      {/* Entity Type Rings */}
      <div className="space-y-4">
        {sunburstData.map((entity, idx) => {
          const percentage = ((entity.total / total) * 100).toFixed(1);
          
          return (
            <div key={entity.type} className="bg-dark-border/20 rounded-lg p-4 border border-dark-border/60">
              {/* Entity Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-accent/20 border border-purple-accent/40 flex items-center justify-center text-xs font-bold text-purple-accent">
                    {idx + 1}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">{entity.type}</div>
                    <div className="text-xs text-gray-500">{entity.total} services ({percentage}%)</div>
                  </div>
                </div>
              </div>

              {/* Health Distribution Ring */}
              <div className="relative h-6 bg-dark-border/40 rounded-full overflow-hidden border border-dark-border/60">
                <div className="absolute inset-0 flex">
                  {entity.healthy > 0 && (
                    <div
                      className="transition-all"
                      style={{
                        width: `${(entity.healthy / entity.total) * 100}%`,
                        backgroundColor: colors.healthy,
                        boxShadow: `inset 0 0 10px ${colors.healthy}40`
                      }}
                      title={`Healthy: ${entity.healthy}`}
                    />
                  )}
                  {entity.degraded > 0 && (
                    <div
                      className="transition-all"
                      style={{
                        width: `${(entity.degraded / entity.total) * 100}%`,
                        backgroundColor: colors.degraded,
                        boxShadow: `inset 0 0 10px ${colors.degraded}40`
                      }}
                      title={`Degraded: ${entity.degraded}`}
                    />
                  )}
                  {entity.unhealthy > 0 && (
                    <div
                      className="transition-all"
                      style={{
                        width: `${(entity.unhealthy / entity.total) * 100}%`,
                        backgroundColor: colors.unhealthy,
                        boxShadow: `inset 0 0 10px ${colors.unhealthy}40`
                      }}
                      title={`Unhealthy: ${entity.unhealthy}`}
                    />
                  )}
                  {entity.unknown > 0 && (
                    <div
                      className="transition-all"
                      style={{
                        width: `${(entity.unknown / entity.total) * 100}%`,
                        backgroundColor: colors.unknown,
                        boxShadow: `inset 0 0 10px ${colors.unknown}40`
                      }}
                      title={`Unknown: ${entity.unknown}`}
                    />
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="mt-2 flex items-center justify-between text-xs">
                <div className="flex items-center gap-3">
                  {entity.healthy > 0 && (
                    <span className="text-lime-accent">{entity.healthy} ✓</span>
                  )}
                  {entity.degraded > 0 && (
                    <span className="text-yellow-accent">{entity.degraded} ⚠</span>
                  )}
                  {entity.unhealthy > 0 && (
                    <span className="text-red-500">{entity.unhealthy} ✗</span>
                  )}
                  {entity.unknown > 0 && (
                    <span className="text-gray-500">{entity.unknown} ?</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-dark-border/50 flex items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-lime-accent"></div>
          <span className="text-gray-400">Healthy</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-accent"></div>
          <span className="text-gray-400">Degraded</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span className="text-gray-400">Unhealthy</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-gray-600"></div>
          <span className="text-gray-400">Unknown</span>
        </div>
      </div>
    </div>
  );
};

export default EntityTypeSunburst;
